import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOpen } from "@mui/icons-material";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as authMod from "firebase/auth";

import AuthPageFrame from "../../../components/AuthPageFrame";
import { ModularAuth } from "../../../components/FirebaseAuth/firebase";
import Loader from "../../../components/Loader";
import { userSignIn } from "../../../libs/user";
import { clearMfaSessionVerified } from "../../../libs/mfa";
import { recordUserActivity } from "../../../libs/activityLog";

const AUTH_ERROR_MESSAGES = {
  "auth/wrong-password": "The email or password you entered is incorrect.",
  "auth/user-not-found": "No account was found for this email address.",
  "auth/invalid-email": "Enter a valid email address.",
  "auth/too-many-requests": "Too many failed attempts. Please wait and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/user-disabled": "This account has been disabled. Contact support for help.",
  "auth/missing-password": "Enter your password to continue.",
  "auth/internal-error": "Something went wrong. Please try again.",
  "auth/invalid-credential": "The email or password you entered is incorrect.",
};

const toFriendlyError = (err, fallback = "Unable to sign in. Please try again.") => {
  const code = err?.code || "";
  if (AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code];

  const raw = String(err?.message || "").trim();
  if (!raw) return fallback;

  // Strip SDK framing: "Firebase: ... (auth/code)."
  const withoutPrefix = raw.replace(/^Firebase:\s*/i, "");
  const withoutCode = withoutPrefix.replace(/\s*\(auth\/[^)]+\)\.?$/i, "");
  return withoutCode || fallback;
};

const SignIn = () => {
  const history = useHistory();
  const location = useLocation();
  const auth = ModularAuth;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaHintIndex, setMfaHintIndex] = useState(0);
  const [verificationId, setVerificationId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [sendingMfaCode, setSendingMfaCode] = useState(false);
  const [verifyingMfaCode, setVerifyingMfaCode] = useState(false);
  const [info, setInfo] = useState("");

  const redirectPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const re = params.get("re");
    if (re && re.startsWith("/")) return re;

    const stored = localStorage.getItem("re");
    if (stored && stored.startsWith("/")) {
      localStorage.removeItem("re");
      return stored;
    }

    return "/";
  }, [location.search]);

  useEffect(() => {
    return () => {
      if (window.mfaRecaptchaVerifier) {
        window.mfaRecaptchaVerifier.clear();
        window.mfaRecaptchaVerifier = null;
      }
    };
  }, []);

  const completeSignIn = async (uid) => {
    if (uid) {
      clearMfaSessionVerified(uid);
    }

    return new Promise((resolve) => {
      userSignIn((result) => {
        if (result) {
          history.replace(redirectPath);
          resolve(true);
        } else {
          setError("Unable to complete sign in logging. Please try again.");
          resolve(false);
        }
      });
    });
  };

  const ensureMfaRecaptcha = async () => {
    if (window.mfaRecaptchaVerifier) {
      return window.mfaRecaptchaVerifier;
    }

    const verifier = new authMod.RecaptchaVerifier(auth, "mfa-recaptcha", {
      size: "normal",
    });

    await verifier.render();
    window.mfaRecaptchaVerifier = verifier;
    return verifier;
  };

  const sendMfaCode = async () => {
    if (!mfaResolver) return;

    setSendingMfaCode(true);
    setError("");
    setInfo("");

    try {
      const hint = mfaResolver.hints[mfaHintIndex];
      if (!hint) {
        throw new Error("No enrolled MFA factor is available for this account.");
      }

      if (hint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
        const verifier = await ensureMfaRecaptcha();
        const provider = new authMod.PhoneAuthProvider(auth);
        const id = await provider.verifyPhoneNumber(
          {
            multiFactorHint: hint,
            session: mfaResolver.session,
          },
          verifier
        );
        setVerificationId(id);
        setInfo("Verification code sent.");
      } else if (hint.factorId === "totp") {
        if (!authMod.TotpMultiFactorGenerator?.assertionForSignIn) {
          throw new Error("Authenticator (TOTP) is not available in the installed Firebase SDK. Please upgrade the firebase package.");
        }
        setVerificationId("totp-ready");
        setInfo("Open your authenticator app and enter the current code.");
      } else {
        throw new Error("Unsupported MFA factor type for sign-in.");
      }
    } catch (err) {
      setError(toFriendlyError(err, "Failed to send MFA verification code."));
    } finally {
      setSendingMfaCode(false);
    }
  };

  const verifyMfaCode = async () => {
    if (!mfaResolver || !mfaCode) return;

    setVerifyingMfaCode(true);
    setError("");

    try {
      const hint = mfaResolver.hints[mfaHintIndex];
      if (!hint) throw new Error("No MFA factor selected.");

      let assertion;
      if (hint.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
        if (!verificationId) throw new Error("Send verification code first.");
        const credential = authMod.PhoneAuthProvider.credential(verificationId, mfaCode);
        assertion = authMod.PhoneMultiFactorGenerator.assertion(credential);
      } else if (hint.factorId === "totp" && authMod.TotpMultiFactorGenerator?.assertionForSignIn) {
        assertion = authMod.TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
      } else {
        throw new Error("Unsupported MFA factor type for verification.");
      }

      const userCredential = await mfaResolver.resolveSignIn(assertion);
      await recordUserActivity("mfa verified", "MFA challenge completed during sign-in");

      await completeSignIn(userCredential.user.uid);
    } catch (err) {
      setError(toFriendlyError(err, "Invalid verification code."));
    } finally {
      setVerifyingMfaCode(false);
    }
  };

  const signInWithPassword = async (event) => {
    event.preventDefault();

    setLoading(true);
    setError("");
    setInfo("");

    try {
      const userCredential = await authMod.signInWithEmailAndPassword(auth, email.trim(), password);
      await completeSignIn(userCredential.user.uid);
    } catch (err) {
      if (err?.code === "auth/multi-factor-auth-required" && err.resolver) {
        setMfaResolver(err.resolver);
        setMfaHintIndex(0);
        setInfo("Multi-factor verification is required for this account.");
        return;
      }
      setError(toFriendlyError(err, "Unable to sign in. Please check your credentials and try again."));
    } finally {
      setLoading(false);
    }
  };

  const mfaStep = !!mfaResolver;

  return (
    <AuthPageFrame
      eyebrow={mfaStep ? "Secure verification" : "Welcome back"}
      title={mfaStep ? "Verify your identity" : "Sign in"}
      subtitle={mfaStep ? "Enter the one-time code from your enrolled MFA device." : "Secure access to your account dashboard."}
    >
      <Box sx={{ width: "100%" }}>
        <Card variant="outlined" sx={{ border: "none", bgcolor: "transparent", boxShadow: "none" }}>
          <CardContent sx={{ p: 0 }}>
            <Stack spacing={1.75}>
              {error && <Alert severity="error">{error}</Alert>}
              {info && <Alert severity="info">{info}</Alert>}
              {!mfaStep ? (
                <Box component="form" onSubmit={signInWithPassword}>
                  <Stack spacing={1.4}>
                    <TextField
                      label="Email"
                      type="email"
                      size="small"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      autoComplete="email"
                      autoFocus
                      style={{marginTop: '8px'}}
                    />

                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      size="small"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                      required
                      autoComplete="current-password"
                      style={{marginTop: '8px'}}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" onClick={() => setShowPassword((s) => !s)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
                      <MuiLink component={Link} to="/forgot-password" variant="body2" underline="hover">
                        Forgot password?
                      </MuiLink>
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      size="medium"
                      startIcon={!loading ? <LockOpen /> : null}
                      disabled={loading}
                      sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, py: 0.95 }}
                    >
                      {loading ? <Loader size={22} /> : "Sign In"}
                    </Button>

                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                      Don&apos;t have an account yet? <MuiLink component={Link} to="/signup">Sign up now</MuiLink>
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {mfaResolver?.hints?.length > 1 && (
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="MFA Factor"
                      value={mfaHintIndex}
                      onChange={(e) => setMfaHintIndex(Number(e.target.value))}
                      fullWidth
                    >
                      {mfaResolver.hints.map((hint, idx) => (
                        <option key={`${hint.uid}-${idx}`} value={idx}>
                          {hint.displayName || "Phone"} {hint.phoneNumber || ""}
                        </option>
                      ))}
                    </TextField>
                  )}

                  {mfaResolver?.hints?.[mfaHintIndex]?.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID && (
                    <div id="mfa-recaptcha" />
                  )}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      onClick={sendMfaCode}
                      disabled={sendingMfaCode || verifyingMfaCode}
                      sx={{ textTransform: "none" }}
                    >
                      {sendingMfaCode ? "Sending..." : "Send code"}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        setMfaResolver(null);
                        setVerificationId("");
                        setMfaCode("");
                        setInfo("");
                        setError("");
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Back to credentials
                    </Button>
                  </Stack>

                  <TextField
                    label={mfaResolver?.hints?.[mfaHintIndex]?.factorId === "totp" ? "Authenticator code" : "Verification code"}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    fullWidth
                  />

                  <Button
                    variant="outlined"
                    onClick={verifyMfaCode}
                    disabled={verifyingMfaCode || !verificationId || !mfaCode}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {verifyingMfaCode ? <Loader size={22} /> : "Verify and continue"}
                  </Button>

                  <Divider />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </AuthPageFrame>
  );
};

export default SignIn;
